import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import NotFoundPage from './NotFoundPage';
import { blogAPI } from '../services/api';
import { safeJsonLd } from '../utils/seo';
import '../styles/pages/blog.css';

// Markdown rendering: lightweight conversion of common patterns.
// Falls back to raw HTML if content already contains tags.
const renderContent = (raw) => {
  if (!raw) return '';
  if (/<\/?[a-z][\s\S]*>/i.test(raw)) return raw; // assume HTML

  // Markdown → HTML (minimal): h2/h3, bold, italic, links, lists, paragraphs
  let html = raw
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim,  '<h2>$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img alt="$1" src="$2" />')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

  // Lists: lines starting with "- "
  html = html.replace(/(?:^|\n)- (.+)(?=\n|$)/g, '\n<li>$1</li>');
  html = html.replace(/(<li>[\s\S]+?<\/li>)/g, '<ul>$1</ul>');

  // Paragraphs: split on double newline
  html = html.split(/\n{2,}/).map((block) => {
    const trimmed = block.trim();
    if (!trimmed) return '';
    if (/^<(h\d|ul|ol|img|blockquote)/i.test(trimmed)) return trimmed;
    return `<p>${trimmed.replace(/\n/g, '<br />')}</p>`;
  }).join('\n');

  return html;
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await blogAPI.getPostBySlug(slug);
        setPost(data.data);
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true);
        else console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="lp-theme blog-page"><Header /><div className="blog-empty">Carregando…</div><Footer /></div>;
  if (notFound || !post) return <NotFoundPage />;

  const title = post.seo_title || post.title;
  const desc  = post.seo_description || post.excerpt || '';
  const img   = post.og_image || post.cover_image;
  const url   = post.canonical_url || `https://lifyup.com.br/blog/${post.slug}`;

  const postJsonLd = {
    '@context': 'https://schema.org',
    '@type': post.schema_type || 'BlogPosting',
    headline: post.title,
    description: desc,
    image: img ? [img] : undefined,
    datePublished: post.published_at,
    dateModified:  post.updated_at,
    author: post.authors
      ? { '@type': 'Person', name: post.authors.name, url: post.authors.slug ? `https://lifyup.com.br/blog/autor/${post.authors.slug}` : undefined }
      : { '@type': 'Organization', name: 'LifeUp' },
    publisher: {
      '@type': 'Organization',
      name: 'LifeUp',
      logo: { '@type': 'ImageObject', url: 'https://lifyup.com.br/assets/icons/logo.svg' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  };

  return (
    <div className="lp-theme blog-page">
      <Helmet>
        <title>{title} — Blog LifeUp</title>
        <meta name="description" content={desc} />
        {post.seo_keywords && <meta name="keywords" content={post.seo_keywords} />}
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        {img && <meta property="og:image" content={img} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc} />
        {img && <meta name="twitter:image" content={img} />}
        {post.published_at && <meta property="article:published_time" content={post.published_at} />}
        {post.updated_at   && <meta property="article:modified_time"  content={post.updated_at} />}
        {post.authors?.name && <meta property="article:author" content={post.authors.name} />}
        <script type="application/ld+json">{safeJsonLd(postJsonLd)}</script>
      </Helmet>

      <Header />
      <article className="blog-post">
        <Link to="/blog" className="blog-filter-chip" style={{ display: 'inline-block', marginBottom: 24 }}>← Voltar ao blog</Link>
        <h1>{post.title}</h1>
        <div className="blog-post__meta">
          {post.authors?.name && <span>{post.authors.name}</span>}
          {post.published_at && <span>· {new Date(post.published_at).toLocaleDateString('pt-BR')}</span>}
          {post.reading_time && <span>· {post.reading_time} min de leitura</span>}
          {post.categories?.name && (
            <Link to={`/blog/categoria/${post.categories.slug}`} className="blog-filter-chip">
              {post.categories.name}
            </Link>
          )}
        </div>

        {post.cover_image && <img src={post.cover_image} alt={post.title} className="blog-post__cover" />}

        <div className="blog-post__content" dangerouslySetInnerHTML={{ __html: renderContent(post.content) }} />

        {post.tags?.length > 0 && (
          <div style={{ marginTop: 48, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {post.tags.map((t) => (
              <Link key={t.id} to={`/blog/tag/${t.slug}`} className="blog-filter-chip">#{t.name}</Link>
            ))}
          </div>
        )}
      </article>
      <Footer />
    </div>
  );
};

export default BlogPostPage;
